/*
 * shopify-cart-kit
 * https://github.com/moghancy/shopify-cart-kit
 *
 * A tiny cart helper for Shopify Online Store 2.0 themes.
 * Wraps the native Shopify Ajax Cart API, performs Section Rendering API
 * swaps, and dispatches a standardized set of cart events.
 *
 * MIT License. Copyright (c) 2026 Amir Moghaddam for Moghancy.
 */
(function (root) {
  'use strict';

  // ---------- internal state ----------

  var defaults = {
    addUrl: null,
    cartUrl: null,
    sections: '',
    sectionsUrl: null,
    legacyEvents: false,
    source: 'shopify-cart-kit'
  };

  var subs = Object.create(null);

  // ---------- helpers ----------

  var endsJs = function (s) { return /\.js(\?|$)/.test(s); };

  var withJs = function (url) {
    if (!url) return null;
    return endsJs(url) ? url : url + '.js';
  };

  var resolveAddUrl = function (override) {
    if (override) return withJs(override);
    if (defaults.addUrl) return withJs(defaults.addUrl);
    var shop = root && root.Shopify;
    if (shop && shop.routes && shop.routes.root) {
      return shop.routes.root.replace(/\/$/, '') + '/cart/add.js';
    }
    return '/cart/add.js';
  };

  var resolveCartUrl = function (override) {
    if (override) return withJs(override);
    if (defaults.cartUrl) return withJs(defaults.cartUrl);
    var shop = root && root.Shopify;
    if (shop && shop.routes && shop.routes.root) {
      return shop.routes.root.replace(/\/$/, '') + '/cart.js';
    }
    return '/cart.js';
  };

  var parseSections = function (input) {
    if (!input) return [];
    if (Array.isArray(input)) return input.map(function (s) { return String(s).trim(); }).filter(Boolean);
    return String(input).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  };

  var dispatch = function (name, detail) {
    var ev = new CustomEvent(name, { detail: detail });
    document.dispatchEvent(ev);
    var list = subs[name];
    if (list) {
      list.slice().forEach(function (fn) {
        try { fn(ev); } catch (e) { console.warn('[cart-kit] subscriber error', name, e); }
      });
    }
  };

  var swapSections = function (sectionsObj) {
    var swapped = [];
    if (!sectionsObj) return swapped;
    Object.keys(sectionsObj).forEach(function (id) {
      var html = sectionsObj[id];
      if (html == null) return;
      var target = document.getElementById('shopify-section-' + id);
      if (!target) return;
      target.outerHTML = html;
      swapped.push(id);
    });
    swapped.forEach(function (id) {
      document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: id } }));
    });
    return swapped;
  };

  var fetchJson = function (url, init) {
    return fetch(url, init).then(function (r) {
      return r.text().then(function (text) {
        var body = null;
        if (text) {
          try { body = JSON.parse(text); } catch (e) { /* leave null */ }
        }
        return { ok: r.ok, status: r.status, body: body };
      });
    });
  };

  // ---------- public API ----------

  var config = function (next) {
    if (next && typeof next === 'object') {
      Object.keys(next).forEach(function (k) {
        if (k in defaults) defaults[k] = next[k];
      });
    }
    return Object.assign({}, defaults);
  };

  var on = function (name, handler) {
    if (!subs[name]) subs[name] = [];
    subs[name].push(handler);
    return function () { off(name, handler); };
  };

  var off = function (name, handler) {
    var list = subs[name];
    if (!list) return;
    var i = list.indexOf(handler);
    if (i !== -1) list.splice(i, 1);
  };

  var get = function (opts) {
    var url = resolveCartUrl(opts && opts.cartUrl);
    return fetchJson(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) {
          var err = new Error('cart-kit: GET cart failed');
          err.status = res.status;
          err.body = res.body;
          throw err;
        }
        return res.body;
      });
  };

  var add = function (opts) {
    opts = opts || {};
    var items = opts.items || [];
    if (!Array.isArray(items) || items.length === 0) {
      return Promise.reject(new Error('cart-kit: items must be a non-empty array'));
    }
    var addUrl = resolveAddUrl(opts.addUrl);
    var cartUrl = resolveCartUrl(opts.cartUrl);
    var sectionList = parseSections(opts.sections != null ? opts.sections : defaults.sections);
    var sectionsUrl = opts.sectionsUrl != null ? opts.sectionsUrl : defaults.sectionsUrl;
    var legacy = opts.legacyEvents != null ? !!opts.legacyEvents : !!defaults.legacyEvents;
    var source = opts.source || defaults.source;

    var reqBody = { items: items };
    if (sectionList.length) reqBody.sections = sectionList.join(',');
    if (sectionsUrl) reqBody.sections_url = sectionsUrl;

    return fetchJson(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(reqBody)
    }).then(function (res) {
      if (!res.ok) {
        var msg = (res.body && (res.body.description || res.body.message)) || 'Could not add to cart.';
        var err = new Error(msg);
        err.status = res.status;
        err.body = res.body;
        err.source = source;
        dispatch('cart:error', { message: msg, status: res.status, body: res.body, source: source });
        throw err;
      }
      var swapped = swapSections(res.body && res.body.sections);
      var added = (res.body && res.body.items) || items;

      return fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (cart) {
          var detail = { items: added, sections: swapped, cart: cart, source: source };
          dispatch('cart:added', detail);
          dispatch('cart:updated', detail);
          if (legacy) {
            dispatch('ajaxProduct:added', detail);
            dispatch('cart:refresh', detail);
            dispatch('cart:open', detail);
          }
          return detail;
        });
    }).catch(function (err) {
      if (err && err.status == null && !err.body) {
        dispatch('cart:error', { message: err.message || 'Network error', error: String(err), source: source });
      }
      throw err;
    });
  };

  // ---------- expose ----------

  root.dvrwCart = {
    version: '0.1.0',
    config: config,
    add: add,
    get: get,
    swapSections: swapSections,
    on: on,
    off: off
  };
})(typeof window !== 'undefined' ? window : this);
